import React from 'react';
import { Upload, Search, CheckCircle, AlertCircle, File, Award, ArrowRight } from 'lucide-react';

const HowItWorks = () => {
  const approvalSteps = [
    {
      id: 1,
      title: 'Submit Your Project',
      description: 'Create an account, upload your PDF drawings and 3D IFC models.',
      icon: <Upload className="text-blue-700" size={24} />,
    },
    {
      id: 2,
      title: 'Level 1 Approval',
      description: 'Initial review of your designs for basic compliance and completeness.',
      icon: <Search className="text-blue-700" size={24} />,
    },
    {
      id: 3,
      title: 'Level 2 Approval',
      description: 'Detailed assessment of technical specifications and safety standards.',
      icon: <Search className="text-blue-700" size={24} />,
    },
    {
      id: 4,
      title: 'Level 3 Approval',
      description: 'Final review by senior approvers to ensure all requirements are met.',
      icon: <CheckCircle className="text-blue-700" size={24} />,
    },
    {
      id: 5,
      title: 'Certificate Issuance',
      description: 'Receive your official Certificate of Approval for your building design.',
      icon: <Award className="text-blue-700" size={24} />,
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Header Banner */}
      <section className="bg-blue-700 py-24 bglog">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl font-bold text-white text-center">How the Approval Process Works</h1>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-16 container mx-auto px-6">
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">The Digital Approval Journey</h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            Our streamlined digital process makes building approvals faster and more efficient.
            Follow these simple steps to get your building design approved.
          </p>
          
          {/* Steps Timeline */}
          <div className="relative mt-20 pt-10">
            {/* Timeline line */}
            <div className="absolute top-0 h-full w-1 bg-blue-100 left-1/2 transform -translate-x-1/2"></div>
            
            {approvalSteps.map((step, index) => (
              <div key={step.id} className="relative mb-20">
                {/* Timeline dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-white font-bold z-10">
                  {step.id}
                </div>
                
                {/* Content box */}
                <div className={`flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-12' : 'text-left pl-12'}`}>
                    <div className="mb-2 flex items-center justify-end">
                      <h3 className="text-xl font-semibold mr-3">{step.title}</h3>
                      <div className="bg-blue-100 p-2 rounded-full">
                        {step.icon}
                      </div>
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  <div className="w-1/12"></div> {/* Spacer */}
                  <div className="w-5/12"></div> {/* Empty space for alternating layout */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Happens During Review */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12">What Happens During the Review</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
              <div className="flex items-start mb-4">
                <File className="text-blue-700 mr-4 mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-semibold mb-2">PDF Document Review</h3>
                  <p className="text-gray-600 mb-4">
                    Our approvers will carefully examine your PDF drawings to verify:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>Dimensional accuracy and scale</li>
                    <li>Compliance with local building codes</li>
                    <li>Structural integrity considerations</li>
                    <li>Safety features and emergency exits</li>
                    <li>Accessibility requirements</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="flex items-start mb-4">
                <File className="text-blue-700 mr-4 mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-semibold mb-2">3D Model Assessment</h3>
                  <p className="text-gray-600 mb-4">
                    The 3D IFC model provides a comprehensive view of your building, allowing approvers to:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    <li>Visualize spatial relationships and clearances</li>
                    <li>Verify building envelope compliance</li>
                    <li>Check mechanical and electrical systems integration</li>
                    <li>Assess environmental impact and energy efficiency</li>
                    <li>Identify potential design conflicts or issues</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Possible Outcomes */}
      <section className="py-16 container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12">Possible Outcomes</h2>
        
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
            <div className="flex items-center mb-4">
              <CheckCircle className="text-green-500 mr-3" size={24} />
              <h3 className="text-xl font-semibold">Approval</h3>
            </div>
            <p className="text-gray-600">
              Your design meets all requirements and standards. You'll receive a Certificate of Approval
              that you can use to proceed with your building project.
            </p>
          </div>
          
          <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
            <div className="flex items-center mb-4">
              <AlertCircle className="text-yellow-500 mr-3" size={24} />
              <h3 className="text-xl font-semibold">Minor Revisions</h3>
            </div>
            <p className="text-gray-600">
              Small changes are needed before approval. You'll receive feedback and can resubmit
              the revised designs without additional fees.
            </p>
          </div>
          
          <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
            <div className="flex items-center mb-4">
              <AlertCircle className="text-red-500 mr-3" size={24} />
              <h3 className="text-xl font-semibold">Major Revisions</h3>
            </div>
            <p className="text-gray-600">
              Significant changes are required. Your project will be returned with detailed feedback,
              and you'll need to address all issues before resubmitting.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-blue-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Submit Your Building Design?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Start your digital approval journey today and experience a faster, more efficient process.
          </p>
          <a href="/register" className="inline-flex items-center bg-white text-blue-700 py-3 px-8 rounded-md font-medium hover:bg-gray-100 transition duration-300">
            Get Started
            <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;